/**
 * @file CacheTags.ts
 * @author Rapoo Device SDK Team
 * @date 2026-04-16
 * @description TaggedCache — Phase 3 缓存装饰器
 *
 * 在 HardwareCache 之上叠加 tag 索引与地址区间索引。
 * 见 design.md `Components §4` / `Phase 3`。
 *
 * --- 职责边界 ---
 *
 * 1. **KV 存储** 委托给 `inner: HardwareCache`:
 *    - get / set / delete / clearAll / size / disable / enable 直接转发
 *    - 本类只加 tag/区间索引的维护逻辑
 *
 * 2. **tag 索引** (tagIndex):
 *    - 一组 `Tag → Set<CacheKey>` 的双向映射
 *    - set 时写入 tagIndex; delete / invalidateByTag / invalidateByAddressRange / clearAll 时清理
 *
 * 3. **地址区间索引** (addressRangeIndex):
 *    - 记录每个 CacheKey 的 `{ addressStart, length }`
 *    - set 时写入; delete / invalidateByAddressRange / clearAll 时清理
 *    - 用于 `invalidateByAddressRange(start, length)` 的精化失效
 *
 * 4. **disable/enable**:
 *    - get/set/delete 转发到 inner 的 disable/enable 状态,本类只维护索引
 *    - clearAll 无视 disable 状态(显式清空意图,design Error Handling §4)
 *
 * 5. **tag 审计**:
 *    - 开发模式下记录所有注册/失效过的 tag,用于发现未配对的 tag
 *
 * @packageDocumentation
 */

import type { CacheEntry, CacheKey, Tag } from './types'
import { HardwareCache } from './HardwareCache'

/** 地址区间元数据结构 */
interface AddressRangeMeta {
  addressStart: number
  length: number
}

/**
 * TaggedCache — HardwareCache 的装饰器,叠加 tag 索引与地址区间索引。
 *
 * 本类与 HardwareCache 的接口兼容,可被 `ReportBackfill` 等组件直接使用。
 * set/delete 时维护双向索引;invalidateByTag / invalidateByAddressRange 按索引批量失效。
 */
export class TaggedCache {
  /**
   * Tag → Set<CacheKey>
   * 同一 tag 可能对应多个缓存条目。
   */
  private readonly tagIndex = new Map<Tag, Set<CacheKey>>()

  /**
   * CacheKey → AddressRangeMeta
   * 仅当 set 时传入了 addressStart/length 才记录。
   */
  private readonly addressRangeIndex = new Map<CacheKey, AddressRangeMeta>()

  /**
   * 缓存启用状态,与内部 HardwareCache 的 enabled 保持同步。
   */
  private enabled = true

  /**
   * 开发模式 Tag 审计开关。启用后记录所有注册/失效过的 tag,用于发现未配对的 tag。
   * 仅在非生产环境有意义,通过 `__enableTagAudit(true/false)` 控制。
   */
  private tagAuditEnabled = false

  /**
   * 所有被 `set()` 注册过的 tag 集合(审计用)。
   */
  private readonly registeredTags = new Set<Tag>()

  /**
   * 所有被 `invalidateByTag()` 调用过的 tag 集合(审计用)。
   */
  private readonly invalidatedTags = new Set<Tag>()

  /**
   * 包装一个 HardwareCache 实例。所有 KV 操作转发给内部 cache,
   * tag/区间索引由 TaggedCache 自己维护。
   */
  constructor(private readonly inner: HardwareCache) {}

  // ---------- 兼容 HardwareCache 的方法,全部转发 ----------

  /**
   * 命中返回 `CacheEntry`,未命中返回 `undefined`。
   * 缓存被禁用时返回 `undefined`。
   */
  get(key: CacheKey): CacheEntry | undefined {
    try {
      if (!this.enabled) return undefined
      return this.inner.get(key)
    } catch (err) {
      console.error('[TaggedCache] get() internal error, ignored:', err)
      return undefined
    }
  }

  /**
   * 写入缓存条目,同时更新 tag 索引与地址区间索引。
   *
   * - `key` 建议使用 `${device.id}:${makeKey(address, length)}` 格式,
   *   确保不同 device 共享 cache 实例时不会交叉命中。
   * - `meta.tags`: 每个 tag 都会被注册到 tagIndex
   * - `meta.addressStart`/`meta.length`: 用于地址区间索引
   */
  set(
    key: CacheKey,
    data: Uint8Array,
    meta?: { tags?: Tag[]; addressStart?: number; length?: number },
  ): void {
    try {
      if (!this.enabled) return

      // 先记旧 tag (用于在 tag 变更时清理旧索引)
      const oldTagKeys = new Set<CacheKey>()

      // 如果 key 已存在且有旧 tag,保留索引清理用
      const existingEntry = this.inner.get(key)
      if (existingEntry) {
        // 从旧 tag 索引中移除这个 key (旧的 tag 关联需清理)
        for (const [, keys] of this.tagIndex) {
          if (keys.has(key)) {
            oldTagKeys.add(key)
          }
        }
      }

      // 写入 HardwareCache
      this.inner.set(key, data)

      // --- 更新 tag 索引 ---
      // 从所有旧 tag 中移除当前 key
      for (const [, keys] of this.tagIndex) {
        keys.delete(key)
      }

      // 注册新 tag
      if (meta?.tags && meta.tags.length > 0) {
        for (const tag of meta.tags) {
          if (tag) {
            let keys = this.tagIndex.get(tag)
            if (!keys) {
              keys = new Set()
              this.tagIndex.set(tag, keys)
            }
            keys.add(key)

            // 审计:记录注册过的 tag
            if (this.tagAuditEnabled) {
              this.registeredTags.add(tag)
            }
          }
        }
      }

      // --- 更新地址区间索引 ---
      if (meta?.addressStart !== undefined && meta?.length !== undefined) {
        this.addressRangeIndex.set(key, {
          addressStart: meta.addressStart,
          length: meta.length,
        })
      } else {
        // 没有地址区间信息的条目,清理可能残留的旧索引
        this.addressRangeIndex.delete(key)
      }
    } catch (err) {
      console.error('[TaggedCache] set() internal error, ignored:', err)
    }
  }

  /**
   * 删除单个缓存条目,同时清理 tag 索引和地址区间索引。
   */
  delete(key: CacheKey): void {
    try {
      if (!this.enabled) return

      this.inner.delete(key)
      this.removeFromIndices(key)
    } catch (err) {
      console.error('[TaggedCache] delete() internal error, ignored:', err)
    }
  }

  /**
   * 缓存条目数量。
   * 缓存被禁用时也返回 0。
   */
  size(): number {
    try {
      if (!this.enabled) return 0
      return this.inner.size()
    } catch (err) {
      console.error('[TaggedCache] size() internal error, ignored:', err)
      return 0
    }
  }

  /**
   * 清空全部缓存条目和索引。
   * 缓存被禁用时也正常执行(显式清空意图,design Error Handling §4)。
   */
  clearAll(): void {
    try {
      this.inner.clearAll()
      this.tagIndex.clear()
      this.addressRangeIndex.clear()
    } catch (err) {
      console.error('[TaggedCache] clearAll() internal error, ignored:', err)
    }
  }

  /**
   * 禁用缓存。禁用后 get 返回 undefined,set/delete 静默丢弃,
   * clearAll 仍正常执行。
   */
  disable(): void {
    this.enabled = false
    this.inner.disable()
  }

  /**
   * 启用缓存。启用后恢复正常 KV 读写。
   */
  enable(): void {
    this.enabled = true
    this.inner.enable()
    this.clearAll()
  }

  // ---------- TaggedCache 独有的方法 ----------

  /**
   * 按 tag 失效:删除所有注册了该 tag 的缓存条目,
   * 同时清理 tag 索引和地址区间索引。
   *
   * 如果 `invalidateByTag` 调用了一个从未被任何缓存条目注册过的 tag,
   * 会在开发模式下输出 `console.warn`(孤儿 tag 警告)。
   *
   * @param tag 要失效的 tag 字符串
   */
  invalidateByTag(tag: Tag): void {
    try {
      if (!this.enabled) return
      if (!tag) return // 空 tag no-op (Requirement 3.10)

      // 开发模式审计:记录被失效的 tag
      if (this.tagAuditEnabled) {
        this.invalidatedTags.add(tag)
      }

      const keys = this.tagIndex.get(tag)
      // 孤儿 tag 警告: 失效侧调用了 tag,但没有任何缓存条目注册过它
      if (!keys || keys.size === 0) {
        // 仅在开发模式下输出,避免生产环境产生噪音
        if (process.env.NODE_ENV !== 'production') {
          console.warn(
            `[TaggedCache] invalidateByTag("${tag}") called but no cached entries have this tag. ` +
              `This may indicate a mismatch: either the read side forgot to register the tag, ` +
              `or there's a typo in the tag name.`,
          )
        }
        return
      }

      // 拷贝一份避免迭代中修改集合
      const keysToDelete = [...keys]
      for (const key of keysToDelete) {
        this.inner.delete(key)
        this.removeFromIndices(key)
      }
    } catch (err) {
      console.error('[TaggedCache] invalidateByTag() internal error, ignored:', err)
    }
  }

  /**
   * 按地址区间失效。删除所有 [entry.addressStart, entry.addressStart + entry.length)
   * 与 [start, start + length) 区间有重叠的条目。
   * 缓存被禁用时静默丢弃。
   *
   * @param start 失效区间起始地址
   * @param length 失效区间长度
   */
  invalidateByAddressRange(start: number, length: number): void {
    try {
      if (!this.enabled) return
      if (!length) return // 零长度区间 no-op
      const end = start + length

      const keysToDelete: CacheKey[] = []
      for (const [key, meta] of this.addressRangeIndex) {
        const entryEnd = meta.addressStart + meta.length
        // 区间重叠判定: [A_start, A_end) ∩ [B_start, B_end) ≠ ∅
        if (meta.addressStart < end && start < entryEnd) {
          keysToDelete.push(key)
        }
      }

      for (const key of keysToDelete) {
        this.inner.delete(key)
        this.removeFromIndices(key)
      }
    } catch (err) {
      console.error('[TaggedCache] invalidateByAddressRange() internal error, ignored:', err)
    }
  }

  /**
   * 从 tag 索引和地址区间索引中移除指定 key。
   *
   * 被 delete / invalidateByTag / invalidateByAddressRange / clearAll 调用。
   * 幂等,key 不在索引中时 no-op。
   */
  private removeFromIndices(key: CacheKey): void {
    try {
      // 从所有 tag 的集合中删掉这个 key
      for (const [, keys] of this.tagIndex) {
        keys.delete(key)
      }
      // 从地址区间索引中删掉这个 key
      this.addressRangeIndex.delete(key)
    } catch (err) {
      console.error('[TaggedCache] removeFromIndices() internal error, ignored:', err)
    }
  }

  // ---------- 开发模式 Tag 审计能力 ----------

  /**
   * 启用/禁用 Tag 审计模式。
   * 启用后,所有被 `set()` 注册过的 tag 和被 `invalidateByTag()` 调用过的 tag 都会被记录,
   * 可在 `close()` 时输出审计报告,帮助发现未配对的 tag。
   *
   * 默认关闭。建议仅在开发/测试环境下开启。
   */
  __enableTagAudit(on: boolean): void {
    this.tagAuditEnabled = on
    if (on) {
      this.registeredTags.clear()
      this.invalidatedTags.clear()
    }
  }

  /**
   * 获取 Tag 审计报告。包含:
   * - `registeredOnly`: 被注册过但从未被失效的 tag (读端写了 tag,但写端从未失效它)
   * - `invalidatedOnly`: 被失效过但从未被注册的 tag (写端失效了 tag,但读端从未注册它)
   *
   * 仅在 Tag 审计模式下有效。非审计模式下返回空报告。
   */
  getTagAuditReport(): { registeredOnly: Tag[]; invalidatedOnly: Tag[] } {
    const registeredOnly: Tag[] = []
    const invalidatedOnly: Tag[] = []

    if (!this.tagAuditEnabled) return { registeredOnly, invalidatedOnly }

    for (const tag of this.registeredTags) {
      if (!this.invalidatedTags.has(tag)) {
        registeredOnly.push(tag)
      }
    }
    for (const tag of this.invalidatedTags) {
      if (!this.registeredTags.has(tag)) {
        invalidatedOnly.push(tag)
      }
    }

    return { registeredOnly, invalidatedOnly }
  }

  // ---------- Shadow Read 调试能力 ----------

  /** @internal Shadow Read 调试开关 */
  get _shadowReadEnabled(): boolean {
    return (this.inner as any).__shadowReadEnabled ?? false
  }

  /**
   * 启用 Shadow Read 模式。
   * 启用后,每次 `get()` 命中缓存时,仍会发起一次真实硬件读,做字节级比对,
   * 不一致时抛错。用于开发调试,验证缓存内容与真实硬件一致。
   *
   * 注意:开启后每次命中都会增加一次硬件读,性能会下降。
   */
  __enableShadowRead(on: boolean): void {
    ;(this.inner as any).__shadowReadEnabled = on
  }
}
